import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  Box,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Typography,
  Divider,
  InputAdornment,
  Collapse,
} from '@mui/material';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import PriceField from './PriceField';
import { db } from '../firebaseConfig';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Autocomplete from '@mui/material/Autocomplete';
import { AppContext } from '../AppContext';

function AddSeed({ open, onClose, onAdd, editMode = false, seedToEdit = {}, setSuccess, setError, }) {

  const { user, userData, seeds, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;
  const defaultCurrency = farmData?.settings?.currency || '£';
  const defaultUnit = farmData?.settings?.weightUnit || 'g';

  // Define state for a single seed document.
  const [newSeed, setNewSeed] = useState({
    name: '',
    seedProvider: '',
    packetWeight: '',
    weightUnit: defaultUnit,
    seedLotNumber: '',
    seedPrice: '',
    priceCurrency: defaultCurrency,
    notes: '',
    soakHours: false,
    dryDay: false,
    germination: '',
    blackout: '',
    wateringFrequency: '',
    wateringIntervalUnit: 'days',
  });

  // State for form validation and errors.
  const [touched, setTouched] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const inlineSelectSx = {
    fontSize: '0.85rem',
    opacity: 0.85,
    '&:hover': { opacity: 1 },
    '&.Mui-focused': { opacity: 1 },
    '& .MuiSelect-icon': { fontSize: 18 },
  };

  const renderInlineSelect = (value, onChange, options) => (
    <Select
      variant="standard"
      disableUnderline
      sx={inlineSelectSx}
      value={value}
      onChange={onChange}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  useEffect(() => {
    if (open) {
      if (editMode && seedToEdit && seedToEdit.id) {
        setNewSeed({
          ...seedToEdit,
          packetWeight: seedToEdit.packetWeight ?? '',
        });
      } else {
        // Reset to defaults for new seed
        setNewSeed({
          name: '',
          seedProvider: '',
          packetWeight: '',
          weightUnit: defaultUnit,
          seedLotNumber: '',
          seedPrice: '',
          priceCurrency: defaultCurrency,
          notes: '',
          soakHours: false,
          dryDay: false,
          germination: '',
          blackout: '',
          wateringFrequency: '',
          wateringIntervalUnit: 'days',
        });
      }
      setFormSubmitted(false);
      setTouched({});
    }
  }, [open, editMode, seedToEdit, defaultCurrency]);

  const handleChange = (field, value) => {
    setNewSeed((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Minimal validation: Require at least the seed's name and grow time.
  const isError = (field) => {
    return !newSeed[field] && (touched[field] || formSubmitted);
  };

  const providerOptions = React.useMemo(() => {
    // Get unique non-empty seedProvider values from your seeds array.
    const providers = seeds
      .map((seed) => seed.seedProvider)
      .filter((provider) => provider && provider.trim() !== '');
    return [...new Set(providers)];
  }, [seeds]);

  const handleSave = async () => {
    setFormSubmitted(true);
    if (!newSeed.name || !newSeed.blackout || !newSeed.germination) {
      setError?.('Please fill in required fields: Name, Germination and Blackout Periods.');
      return;
    }
    if (!user?.uid) {
      setError?.('User not logged in.');
      return;
    }
    if (!activeFarmId) {
      setError?.('No active farm selected.');
      return;
    }
  
    // Parse packetWeight only if user entered a value.
    const packetWeightInput = String(newSeed.packetWeight ?? '').trim();
    const hasPacketWeight = packetWeightInput !== '';
    const raw = hasPacketWeight ? parseFloat(packetWeightInput) : null;
    let saveValue = raw;
    let saveUnit = newSeed.weightUnit;

    // Normalize units only when a packet weight exists.
    if (hasPacketWeight && saveUnit === 'kg') {
      saveValue = raw * 1000;
      saveUnit = 'g';
    } else if (hasPacketWeight && saveUnit === 'lb') {
      saveValue = raw * 16;
      saveUnit = 'oz';
    }
  
    // 3) Build a payload that overrides just those two fields
    const payload = {
      ...newSeed,
      packetWeight: hasPacketWeight ? saveValue : null,
      weightUnit: saveUnit,
    };
  
    try {
      if (editMode && seedToEdit.id) {
        await updateDoc(
          doc(db, 'farms', activeFarmId, 'seeds', seedToEdit.id),
          payload
        );
        setSuccess?.('Seed updated successfully.');
      } else {
        await addDoc(
          collection(db, 'farms', activeFarmId, 'seeds'),
          payload
        );
        setSuccess?.('Seed added successfully.');
      }
      if (onAdd) onAdd(payload);
      onClose();
    } catch (error) {
      setError?.('Error saving seed.');
    }
  };
  


  const handleCloseDialog = () => {
    setFormSubmitted(false);
    setTouched({});
    onClose();
  };

  const handleDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseDialog();
      return;
    }

    if (e.key !== 'Enter') return;
    if (e.defaultPrevented || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'textarea' || target?.isContentEditable) return;
    if (target?.closest?.('[role="listbox"], [role="menu"]')) return;

    e.preventDefault();
    handleSave();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseDialog} onKeyDown={handleDialogKeyDown} fullWidth>
        <DialogTitle>{editMode ? 'Edit Seed' : 'Add New Seed'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={7}>
              <TextField
                label="Name"
                fullWidth
                required
                value={newSeed.name}
                onChange={(e) => handleChange('name', e.target.value)}
                onBlur={() => handleBlur('name')}
                error={isError('name')}
                helperText={isError('name') ? 'Required' : ''}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                label="Germination (days)"
                fullWidth
                required
                value={newSeed.germination}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    handleChange('germination', val);
                  }
                }}
                onBlur={() => handleBlur('germination')}
                error={isError('germination')}
                helperText={isError('germination') ? 'Required' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>How many days from sowing to blackout period.</Typography>}>
                        <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Blackout (days)"
                fullWidth
                required
                value={newSeed.blackout}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    handleChange('blackout', val);
                  }
                }}
                onBlur={() => handleBlur('blackout')}
                error={isError('blackout')}
                helperText={isError('blackout') ? 'Required' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>Number of days in blackout before trays go under lights.</Typography>}>
                        <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Water Every"
                fullWidth
                required
                value={newSeed.wateringFrequency}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    setNewSeed((prev) => ({
                      ...prev,
                      wateringFrequency: val,
                    }));
                  }
                }}
                onBlur={() => handleBlur('wateringFrequency')}
                error={isError('wateringFrequency')}
                helperText={isError('wateringFrequency') ? 'Required' : ''}
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderInlineSelect(
                          newSeed.wateringIntervalUnit,
                          (e) =>
                            setNewSeed((prev) => ({
                              ...prev,
                              wateringIntervalUnit: e.target.value,
                            })),
                          [
                            { value: 'days', label: 'Day(s)' },
                            { value: 'hours', label: 'Hour(s)' },
                          ]
                        )}
                        <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>Interval between waterings. Increase for systems like flood-and-drain reservoirs.</Typography>}>
                          <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                        </Tooltip>
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                onClick={() => setShowOptional((v) => !v)}
                role="button"
                tabIndex={0}
                aria-expanded={showOptional}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setShowOptional((v) => !v);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  px: 2,
                  py: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: 'action.hover',
                  '&:hover': { backgroundColor: 'action.selected' },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Optional
                  </Typography>
                  {!showOptional && (
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Provider, lot number, packet weight/cost, dry off day, soak hours, notes
                    </Typography>
                  )}
                </Box>
                <KeyboardArrowDownIcon
                  sx={{
                    transition: 'transform 150ms ease',
                    transform: showOptional ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Collapse in={showOptional}>
            <Divider sx={{ my: 1.5, opacity: 0.2 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={providerOptions}
                  value={newSeed.seedProvider || ''}
                  onChange={(event, newValue) => handleChange('seedProvider', newValue)}
                  onInputChange={(event, newInputValue) => handleChange('seedProvider', newInputValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Provider / Brand"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Lot Number"
                  fullWidth
                  value={newSeed.seedLotNumber}
                  onChange={(e) => handleChange('seedLotNumber', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Packet Weight"
                  fullWidth
                  value={newSeed.packetWeight}
                  onChange={(e) => handleChange('packetWeight', e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {renderInlineSelect(
                          newSeed.weightUnit || defaultUnit,
                          (e) => handleChange('weightUnit', e.target.value),
                          [
                            { value: 'g', label: 'g' },
                            { value: 'kg', label: 'kg' },
                            { value: 'oz', label: 'oz' },
                            { value: 'lb', label: 'lb' },
                          ]
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <PriceField
                  label="Packet Cost"
                  fullWidth
                  value={newSeed.seedPrice || ''}
                  onChange={(value) => handleChange('seedPrice', value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {renderInlineSelect(
                        newSeed.priceCurrency || defaultCurrency,
                        (e) => handleChange('priceCurrency', e.target.value),
                        [
                          { value: '£', label: '£' },
                          { value: '$', label: '$' },
                          { value: '€', label: '€' },
                        ]
                      )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newSeed.dryDay}
                      onChange={(e) => handleChange('dryDay', e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Dry off day</span>
                      <Tooltip title={<Typography sx={{ fontSize: '0.8rem' }}>Forego watering one day before harvest to help prevent mold formation.</Typography>}>
                        <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                      </Tooltip>
                    </Box>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={7}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newSeed.soakHours !== false}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleChange('soakHours', '');
                            } else {
                              handleChange('soakHours', false);
                            }
                          }}
                        />
                      }
                      label="Soak Seeds"
                      sx={{ m: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Soak Hours"
                      value={newSeed.soakHours === false ? '' : newSeed.soakHours}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val)) handleChange('soakHours', val);
                      }}
                      inputProps={{ inputMode: 'numeric' }}
                      size="small"
                      disabled={newSeed.soakHours === false}
                      sx={{
                        maxWidth: 140,
                        opacity: newSeed.soakHours === false ? 0 : 1,
                        pointerEvents: newSeed.soakHours === false ? 'none' : 'auto',
                        transition: 'opacity 150ms ease',
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  minRows={2}
                  value={newSeed.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                />
              </Grid>
            </Grid>
          </Collapse>
</DialogContent>

        <DialogActions>
          <Button variant="text" onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={handleSave}>
            {editMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AddSeed;



